import type { ApiClient } from '../api/client.js';
import type { DesktopBridge } from '../session/controller.js';

export interface ComputerStatusSync { stop(): void }

export function startComputerStatusSync(
  api: Pick<ApiClient, 'updateComputerStatus'>,
  bridge: DesktopBridge['computer'] | undefined,
  options: { intervalMs?: number; document?: Document; window?: Window } = {},
): ComputerStatusSync {
  if (!bridge) return { stop() { /* web and mobile are intentionally inert */ } };
  const doc = options.document ?? globalThis.document;
  const win = options.window ?? globalThis.window;
  const intervalMs = options.intervalMs ?? 60_000;
  let stopped = false;
  let sending = false;
  let lastAttempt = 0;
  let request: AbortController | null = null;
  const foreground = () => doc.visibilityState !== 'hidden' && (typeof doc.hasFocus !== 'function' || doc.hasFocus());

  const send = async (force = false): Promise<void> => {
    if (stopped || sending || !foreground()) return;
    const now = Date.now();
    if (!force && now - lastAttempt < intervalMs) return;
    sending = true; lastAttempt = now;
    request = new AbortController();
    try {
      const status = await bridge.status();
      if (!stopped) await api.updateComputerStatus(status, request.signal);
    } catch (error) {
      if (!stopped && !(error instanceof DOMException && error.name === 'AbortError')) console.warn('[ASIS] No se pudo sincronizar el estado del Mac; se reintentará.');
    }
    finally { request = null; sending = false; }
  };
  const onForeground = () => { void send(false); };
  doc.addEventListener('visibilitychange', onForeground);
  win.addEventListener('focus', onForeground);
  const timer = win.setInterval(() => { void send(true); }, intervalMs);
  void send(true);
  return {
    stop() {
      if (stopped) return;
      stopped = true;
      request?.abort(); request = null;
      win.clearInterval(timer);
      doc.removeEventListener('visibilitychange', onForeground);
      win.removeEventListener('focus', onForeground);
    },
  };
}
